import React from "react";
import { v4 } from "uuid";
import { HttpEventType } from "@angular/common/http";
import { usePcpApi } from "./usePcpApi";
import { AttachmentMetadata, AttachmentMetadataInProgress } from "@azure/communication-react";

// Maximum size (per file) accepted for chat attachment uploads. This is a client-side
// guard for UX feedback; the backend should still enforce its own limit.
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type PendingUpload = { file: File; id: string };

// Stable, collision-free, CSS-selector-safe key used for an attachment across selection,
// progress and completion. FluentUI's AttachmentCard injects this id verbatim into a
// `#attachment-<id>` querySelector, so it must not contain spaces, dots or `%`. The old
// `${v4()}-${encodeURI(name)}` form crashed the receiver on click; a plain uuid is safe
// and the display name is kept separately in `AttachmentMetadata.name`.
const makeAttachmentId = (): string => v4();

export const useChatFileUpload = () => {
    const api = usePcpApi();

    // We use a ref variable to keep a track of all the active file uploads and their progress.
    // Since a ref variable preserves it's value across re-renders, it ensures that modifying the progress of one file upload
    // doesn't overwrite the other file uploads.
    const allAttachmentsWithProgress = React.useRef<AttachmentMetadataInProgress[]>([]);
    // We use a ref variable to keep a track of all the completed file uploads since a ref variable preserves it's state
    // across re-renders.
    const completedFileUploads = React.useRef<AttachmentMetadata[] | []>([]);
    // Tracks the pending uploads (valid, non-oversized files) selected by the file input,
    // each paired with the unique id used as its attachment key across progress + completion.
    const [pendingUploads, setPendingUploads] = React.useState<PendingUpload[]>([]);
    // Tracks the progress of the file uploads. Passed to SendBox component for driving file upload UI.
    const [attachmentsWithProgress, setAttachmentsWithProgress] = React.useState<AttachmentMetadataInProgress[]>([]);

    const onCancelFileUpload = (fileId: string) => {
        allAttachmentsWithProgress.current = allAttachmentsWithProgress.current.filter((upload) => upload.id !== fileId);
        setAttachmentsWithProgress(allAttachmentsWithProgress.current);
    };

    const updateFileUploadProgress = (fileId: string, progress: number, complete: boolean = false) => {
        allAttachmentsWithProgress.current = updateProgressForOneFile(
            allAttachmentsWithProgress.current,
            fileId,
            progress,
            complete
        );
        setAttachmentsWithProgress(allAttachmentsWithProgress.current);
    };

    const completeFileUpload = (fileId: string, attachmentMetadata: AttachmentMetadata) => {
        updateFileUploadProgress(fileId, 1, true);
        completedFileUploads.current = [...completedFileUploads.current, attachmentMetadata];
    };

    // Surface a failed upload in the SendBox (via the `error` field) instead of leaving the
    // progress bar silently stalled. The file never reaches `completedFileUploads`, so it is
    // not included when the message is sent.
    const failFileUpload = (fileId: string) => {
        allAttachmentsWithProgress.current = allAttachmentsWithProgress.current.map((upload) =>
            upload.id === fileId
                ? { ...upload, error: { message: `„${upload.name}" konnte nicht hochgeladen werden.` } }
                : upload
        );
        setAttachmentsWithProgress(allAttachmentsWithProgress.current);
    };

    const uploadFile = async ({ file, id }: PendingUpload): Promise<void> => {
        const data = new FormData();
        data.append("file", file);

        api.postProgress<{ url: string }, unknown>(
            `/api/live-lesson/chat/upload-file`,
            data,
            {
                onUploadProgress: (event) => {
                    if (event.type === HttpEventType.UploadProgress && event.total !== undefined) {
                        updateFileUploadProgress(id, event.loaded / event.total);
                    }
                },
            }
        )
            .then(res => {
                completeFileUpload(id, {
                    id,
                    name: file.name,
                    url: res.body!.url!,
                });
            })
            .catch((err) => {
                console.error('File upload failed:', err);
                failFileUpload(id);
            });
    };

    // Triggered whenever new files are selected by the file input.
    const selectFileAttachment = (fileList: FileList | null) => {
        if (!fileList) return;

        const progress: AttachmentMetadataInProgress[] = [];
        const uploads: PendingUpload[] = [];

        for (const file of Array.from(fileList)) {
            const id = makeAttachmentId();
            if (file.size > MAX_FILE_SIZE_BYTES) {
                // Oversized files are surfaced with an error in the SendBox and never uploaded.
                progress.push({
                    name: file.name,
                    id,
                    progress: 0,
                    error: { message: `„${file.name}" ist zu groß. Die maximale Dateigröße beträgt ${MAX_FILE_SIZE_MB} MB.` },
                });
            } else {
                progress.push({ name: file.name, id, progress: 0 });
                uploads.push({ file, id });
            }
        }

        allAttachmentsWithProgress.current = progress;
        setAttachmentsWithProgress(progress);
        setPendingUploads(uploads);
    };

    // We trigger `uploadFile` whenever new files are selected.
    React.useEffect(() => {
        if (pendingUploads.length) {
            pendingUploads.forEach(uploadFile);
        }
        // We only need to run this useEffect when new files are selected by the file input.
        // And hence, we don't add `uploadFile` to dependency array.
    }, [pendingUploads]);


    const resetAttachments = () => {
        allAttachmentsWithProgress.current = [];
        completedFileUploads.current = [];
        setAttachmentsWithProgress([]);
    };

    return {
        onCancelFileUpload,
        attachmentsWithProgress,
        completedFileUploads,
        resetAttachments,
        selectFileAttachment,
    };
};

const updateProgressForOneFile = (
    allAttachmentsWithProgress: AttachmentMetadataInProgress[],
    fileId: string,
    progress: number,
    complete: boolean
) => allAttachmentsWithProgress.map((active) => {
    if (active.id === fileId) {
        return {
            ...active,
            progress,
            uploadComplete: complete,
        };
    }
    return active;
});
